import { AkamaiColor } from '@gonfalon/sdk-logos/icons/AkamaiColor';
import { Android } from '@gonfalon/sdk-logos/icons/Android';
import { CloudflareColor } from '@gonfalon/sdk-logos/icons/CloudflareColor';
import { CPPColor } from '@gonfalon/sdk-logos/icons/CPPColor';
import { DotNetColor } from '@gonfalon/sdk-logos/icons/DotNetColor';
import { Electron } from '@gonfalon/sdk-logos/icons/Electron';
import { ErlangColor } from '@gonfalon/sdk-logos/icons/ErlangColor';
import { Flutter } from '@gonfalon/sdk-logos/icons/Flutter';
import { GoColor } from '@gonfalon/sdk-logos/icons/GoColor';
import { HaskellColor } from '@gonfalon/sdk-logos/icons/HaskellColor';
import { IosColor } from '@gonfalon/sdk-logos/icons/IosColor';
import { JavaColor } from '@gonfalon/sdk-logos/icons/JavaColor';
import { JavaScript } from '@gonfalon/sdk-logos/icons/JavaScript';
import { LuaColor } from '@gonfalon/sdk-logos/icons/LuaColor';
import { Node } from '@gonfalon/sdk-logos/icons/Node';
import { PHPColor } from '@gonfalon/sdk-logos/icons/PHPColor';
import { PythonColor } from '@gonfalon/sdk-logos/icons/PythonColor';
import { ReactJS } from '@gonfalon/sdk-logos/icons/ReactJS';
import { RokuColor } from '@gonfalon/sdk-logos/icons/RokuColor';
import { RubyColor } from '@gonfalon/sdk-logos/icons/RubyColor';
import { RustColor } from '@gonfalon/sdk-logos/icons/RustColor';
import { SalesforceColor } from '@gonfalon/sdk-logos/icons/SalesforceColor';
import { VercelWhiteColor } from '@gonfalon/sdk-logos/icons/VercelWhiteColor';
import { VueColor } from '@gonfalon/sdk-logos/icons/VueColor';

import {
  androidExperimentationButtonCopy,
  androidInit,
  androidInstallGroovy,
  androidInstallKotlin,
} from './snippets/android-client-sdk';
import { dotnetServerInit, dotnetServerInstallCsharp } from './snippets/dotnet-server-sdk';
import { goInit, goInstall } from './snippets/go-server-sdk';
import {
  iosExperimentationButtonCopy,
  iosInit,
  iosInstallCartfile,
  iosInstallPackageSwift,
  iosInstallPodfile,
} from './snippets/ios-client-sdk';
import { javaFlagEval, javaInit, javaInstallGradle, javaInstallXml } from './snippets/java-server-sdk';
import {
  jsExperimentationButtonCopy,
  jsFlagEval,
  jsInit,
  jsInstallNpm,
  jsInstallPnpm,
  jsInstallYarn,
} from './snippets/js-client-sdk';
import {
  nodeClientInit,
  nodeClientInstallNpm,
  nodeClientInstallPnpm,
  nodeClientInstallYarn,
} from './snippets/node-client-sdk';
import {
  nodeServerFlagEval,
  nodeServerInit,
  nodeServerInstallNpm,
  nodeServerInstallPnpm,
  nodeServerInstallYarn,
} from './snippets/node-server-sdk';
import { pythonFlagEval, pythonInit, pythonInstallPip } from './snippets/python-server-sdk';
import {
  reactExperimentationButtonCopy,
  reactFlagEval,
  reactInit,
  reactInstallNpm,
  reactInstallPnpm,
  reactInstallYarn,
} from './snippets/react-client-sdk';
import {
  reactNativeExperimentationButtonCopy,
  reactNativeInit,
  reactNativeInstallNpm,
  reactNativeInstallNpmAsyncStorage,
  reactNativeInstallPnpm,
  reactNativeInstallPnpmAsyncStorage,
  reactNativeInstallYarn,
  reactNativeInstallYarnAsyncStorage,
} from './snippets/react-native-client-sdk';
import { vueInit, vueInstallNpm, vueInstallPnpm, vueInstallYarn } from './snippets/vue-client-sdk';
import type { SdkInfo, SdkInitKey } from './types';

/**
 * Helper to create initKey arrays with widened type (avoids tuple narrowing from `as const`)
 */
const initKeys = (...keys: SdkInitKey[]): readonly SdkInitKey[] => keys;

/**
 * SDK metadata map
 *
 * Keys follow the pattern: {platform}-{client|server}-sdk
 * Example: 'android-client-sdk', 'node-server-sdk'
 *
 * Please do not import this directly, instead use helper functions ex. getSdkInfoByKey, getSdkInstallSnippet
 * Doing so improves our bundle size. Thank you!
 *
 */
export const SDK_INFO = {
  'akamai-server-edgekv-sdk': {
    key: 'akamai-server-edgekv-sdk',
    name: 'Akamai',
    icon: AkamaiColor,
    sdkType: 'Edge',
    initKey: initKeys('clientSide'),
    docsKey: 'akamai',
    docsPath: 'sdk/edge/akamai',
    prismLang: 'javascript',
    tags: ['legacy'],
  },
  'android-client-sdk': {
    key: 'android-client-sdk',
    name: 'Android',
    icon: Android,
    sdkType: 'Client-side',
    initKey: initKeys('mobile'),
    docsKey: 'android',
    docsPath: 'sdk/client-side/android',
    prismLang: 'kotlin',
    initSnippet: androidInit,
    installSnippets: {
      groovy: androidInstallGroovy,
      kotlin: androidInstallKotlin,
    },
    experimentationSnippet: {
      trackOnly: androidExperimentationButtonCopy,
    },
    tags: ['popular', 'mobile'],
    observabilitySupported: true,
  },
  'apex-server-sdk': {
    key: 'apex-server-sdk',
    name: 'Apex',
    icon: SalesforceColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'apex',
    docsPath: 'sdk/server-side/apex',
    prismLang: 'apex',
    tags: ['legacy'],
  },
  'cpp-client-sdk': {
    key: 'cpp-client-sdk',
    name: 'C++ (Client)',
    icon: CPPColor,
    sdkType: 'Client-side',
    initKey: initKeys('mobile'),
    docsKey: 'c-c--',
    docsPath: 'sdk/client-side/c-c--',
    prismLang: 'cpp',
    tags: ['mobile', 'legacy'],
  },
  'cpp-server-sdk': {
    key: 'cpp-server-sdk',
    name: 'C++ (Server)',
    icon: CPPColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'c-c--',
    docsPath: 'sdk/server-side/c-c--',
    prismLang: 'cpp',
    tags: ['legacy'],
  },
  'cloudflare-server-sdk': {
    key: 'cloudflare-server-sdk',
    name: 'Cloudflare',
    icon: CloudflareColor,
    sdkType: 'Edge',
    initKey: initKeys('clientSide'),
    docsKey: 'cloudflare',
    docsPath: 'sdk/edge/cloudflare',
    prismLang: 'javascript',
    tags: ['legacy'],
  },
  'electron-client-sdk': {
    key: 'electron-client-sdk',
    name: 'Electron',
    icon: Electron,
    sdkType: 'Client-side',
    initKey: initKeys('clientSide'),
    docsKey: 'electron',
    docsPath: 'sdk/client-side/electron',
    prismLang: 'javascript',
    tags: ['legacy'],
  },
  'erlang-server-sdk': {
    key: 'erlang-server-sdk',
    name: 'Erlang',
    icon: ErlangColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'erlang',
    docsPath: 'sdk/server-side/erlang',
    prismLang: 'erlang',
    tags: ['legacy'],
  },
  'flutter-client-sdk': {
    key: 'flutter-client-sdk',
    name: 'Flutter',
    icon: Flutter,
    sdkType: 'Client-side',
    initKey: initKeys('clientSide', 'mobile'),
    docsKey: 'flutter',
    docsPath: 'sdk/client-side/flutter',
    prismLang: 'dart',
    tags: ['mobile'],
    wrapperName: 'FlutterClientSdk',
  },
  'go-server-sdk': {
    key: 'go-server-sdk',
    name: 'Go',
    icon: GoColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'go',
    docsPath: 'sdk/server-side/go',
    prismLang: 'go',
    initSnippet: goInit,
    installSnippets: {
      go: goInstall,
    },
    tags: ['popular'],
    observabilitySupported: true,
  },
  'haskell-server-sdk': {
    key: 'haskell-server-sdk',
    name: 'Haskell',
    icon: HaskellColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'haskell',
    docsPath: 'sdk/server-side/haskell',
    prismLang: 'haskell',
  },
  'ios-client-sdk': {
    key: 'ios-client-sdk',
    name: 'iOS',
    icon: IosColor,
    sdkType: 'Client-side',
    initKey: initKeys('mobile'),
    docsKey: 'ios',
    docsPath: 'sdk/client-side/ios',
    prismLang: 'swift',
    initSnippet: iosInit,
    installSnippets: {
      packageSwift: iosInstallPackageSwift,
      podfile: iosInstallPodfile,
      cartfile: iosInstallCartfile,
    },
    experimentationSnippet: {
      trackOnly: iosExperimentationButtonCopy,
    },
    tags: ['popular', 'mobile'],
    observabilitySupported: true,
  },
  'java-server-sdk': {
    key: 'java-server-sdk',
    name: 'Java',
    icon: JavaColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'java',
    docsPath: 'sdk/server-side/java',
    prismLang: 'java',
    initSnippet: javaInit,
    installSnippets: {
      xml: javaInstallXml,
      gradle: javaInstallGradle,
    },
    flagEvalSnippet: javaFlagEval,
    tags: ['popular'],
    observabilitySupported: true,
  },
  'js-client-sdk': {
    key: 'js-client-sdk',
    name: 'JavaScript',
    icon: JavaScript,
    sdkType: 'Client-side',
    initKey: initKeys('clientSide'),
    docsKey: 'javascript',
    docsPath: 'sdk/client-side/javascript',
    prismLang: 'javascript',
    initSnippet: jsInit,
    installSnippets: {
      yarn: jsInstallYarn,
      npm: jsInstallNpm,
      pnpm: jsInstallPnpm,
    },
    flagEvalSnippet: jsFlagEval,
    experimentationSnippet: {
      trackOnly: jsExperimentationButtonCopy,
    },
    tags: ['popular'],
    observabilitySupported: true,
  },
  'lua-server-sdk': {
    key: 'lua-server-sdk',
    name: 'Lua',
    icon: LuaColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'lua',
    docsPath: 'sdk/server-side/lua',
    prismLang: 'lua',
    tags: ['legacy'],
  },
  'dotnet-client-sdk': {
    key: 'dotnet-client-sdk',
    name: '.NET (Client)',
    icon: DotNetColor,
    sdkType: 'Client-side',
    initKey: initKeys('mobile'),
    docsKey: 'dotnet',
    docsPath: 'sdk/client-side/dotnet',
    prismLang: 'csharp',
    tags: ['mobile', 'legacy'],
  },
  'dotnet-server-sdk': {
    key: 'dotnet-server-sdk',
    name: '.NET (Server)',
    icon: DotNetColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'dotnet',
    docsPath: 'sdk/server-side/dotnet',
    prismLang: 'csharp',
    initSnippet: dotnetServerInit,
    installSnippets: {
      csharp: dotnetServerInstallCsharp,
    },
    tags: ['popular'],
    observabilitySupported: true,
  },
  'node-client-sdk': {
    key: 'node-client-sdk',
    name: 'Node.js (Client)',
    icon: Node,
    sdkType: 'Client-side',
    initKey: initKeys('clientSide'),
    docsKey: 'node-js',
    docsPath: 'sdk/client-side/node-js',
    prismLang: 'javascript',
    initSnippet: nodeClientInit,
    installSnippets: {
      yarn: nodeClientInstallYarn,
      npm: nodeClientInstallNpm,
      pnpm: nodeClientInstallPnpm,
    },
    tags: ['mobile', 'legacy'],
  },
  'node-server-sdk': {
    key: 'node-server-sdk',
    name: 'Node.js (Server)',
    icon: Node,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'node-js',
    docsPath: 'sdk/server-side/node-js',
    prismLang: 'typescript',
    initSnippet: nodeServerInit,
    installSnippets: {
      yarn: nodeServerInstallYarn,
      npm: nodeServerInstallNpm,
      pnpm: nodeServerInstallPnpm,
    },
    flagEvalSnippet: nodeServerFlagEval,
    tags: ['popular'],
    observabilitySupported: true,
  },
  'php-server-sdk': {
    key: 'php-server-sdk',
    name: 'PHP',
    icon: PHPColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'php',
    docsPath: 'sdk/server-side/php',
    prismLang: 'php',
  },
  'python-server-sdk': {
    key: 'python-server-sdk',
    name: 'Python',
    icon: PythonColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'python',
    docsPath: 'sdk/server-side/python',
    prismLang: 'python',
    initSnippet: pythonInit,
    installSnippets: {
      pip: pythonInstallPip,
    },
    flagEvalSnippet: pythonFlagEval,
    tags: ['popular'],
    observabilitySupported: true,
  },
  'react-client-sdk': {
    key: 'react-client-sdk',
    name: 'React Web',
    icon: ReactJS,
    sdkType: 'Client-side',
    initKey: initKeys('clientSide'),
    docsKey: 'react',
    docsPath: 'sdk/client-side/react',
    prismLang: 'tsx',
    initSnippet: reactInit,
    installSnippets: {
      yarn: reactInstallYarn,
      npm: reactInstallNpm,
      pnpm: reactInstallPnpm,
    },
    flagEvalSnippet: reactFlagEval,
    experimentationSnippet: {
      trackOnly: reactExperimentationButtonCopy,
    },
    tags: ['popular'],
    observabilitySupported: true,
    wrapperName: 'react-client-sdk',
  },
  'react-native-client-sdk': {
    key: 'react-native-client-sdk',
    name: 'React Native',
    icon: ReactJS,
    sdkType: 'Client-side',
    initKey: initKeys('mobile'),
    docsKey: 'react-native',
    docsPath: 'sdk/client-side/react-native',
    prismLang: 'tsx',
    initSnippet: reactNativeInit,
    installSnippets: {
      yarn: reactNativeInstallYarn,
      npm: reactNativeInstallNpm,
      pnpm: reactNativeInstallPnpm,
      asyncStorage: {
        yarn: reactNativeInstallYarnAsyncStorage,
        npm: reactNativeInstallNpmAsyncStorage,
        pnpm: reactNativeInstallPnpmAsyncStorage,
      },
    },
    experimentationSnippet: {
      trackOnly: reactNativeExperimentationButtonCopy,
    },
    tags: ['popular', 'mobile'],
    observabilitySupported: true,
  },
  'roku-client-sdk': {
    key: 'roku-client-sdk',
    name: 'Roku',
    icon: RokuColor,
    sdkType: 'Client-side',
    initKey: initKeys('mobile'),
    docsKey: 'roku',
    docsPath: 'sdk/client-side/roku',
    prismLang: 'brightscript',
    tags: ['mobile'],
  },
  'ruby-server-sdk': {
    key: 'ruby-server-sdk',
    name: 'Ruby / Rails',
    icon: RubyColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'ruby',
    docsPath: 'sdk/server-side/ruby',
    prismLang: 'ruby',
  },
  'rust-server-sdk': {
    key: 'rust-server-sdk',
    name: 'Rust',
    icon: RustColor,
    sdkType: 'Server-side',
    initKey: initKeys('serverSide'),
    docsKey: 'rust',
    docsPath: 'sdk/server-side/rust',
    prismLang: 'rust',
  },
  'vercel-server-sdk': {
    key: 'vercel-server-sdk',
    name: 'Vercel',
    icon: VercelWhiteColor,
    sdkType: 'Edge',
    initKey: initKeys('clientSide'),
    docsKey: 'vercel',
    docsPath: 'sdk/edge/vercel',
    prismLang: 'javascript',
    tags: ['legacy'],
  },
  'vue-client-sdk': {
    key: 'vue-client-sdk',
    name: 'Vue',
    icon: VueColor,
    sdkType: 'Client-side',
    initKey: initKeys('clientSide'),
    docsKey: 'vue',
    docsPath: 'sdk/client-side/vue',
    prismLang: 'javascript',
    initSnippet: vueInit,
    installSnippets: {
      yarn: vueInstallYarn,
      npm: vueInstallNpm,
      pnpm: vueInstallPnpm,
    },
    observabilitySupported: true,
    wrapperName: 'vue-client-sdk',
  },
} as const satisfies Record<string, SdkInfo>;

/* Popular SDK keys - curated order for UI display */
export const POPULAR_SDK_KEYS = [
  'react-client-sdk',
  'js-client-sdk',
  'node-server-sdk',
  'python-server-sdk',
  'react-native-client-sdk',
  'dotnet-server-sdk',
  'java-server-sdk',
  'go-server-sdk',
  'ios-client-sdk',
  'android-client-sdk',
] as const satisfies readonly SdkKey[];

/* Popular SDK Keys */
export const POPULAR_SDKS = POPULAR_SDK_KEYS.map((key) => SDK_INFO[key]) as readonly SdkInfo[];

/* Mobile SDK keys */
export const MOBILE_SDK_KEYS = Object.entries(SDK_INFO)
  .filter(([_, sdk]) => 'tags' in sdk && Array.isArray(sdk.tags) && sdk.tags?.includes('mobile'))
  .map(([key]) => key) as readonly SdkKey[];

/* Legacy SDK keys */
export const LEGACY_SDK_KEYS = Object.entries(SDK_INFO)
  .filter(([_, sdk]) => 'tags' in sdk && Array.isArray(sdk.tags) && sdk.tags?.includes('legacy'))
  .map(([key]) => key) as readonly SdkKey[];

/* Server-side SDKs */
const serverSideEntries = Object.entries(SDK_INFO).filter(([_, sdk]) => sdk.sdkType === 'Server-side');
export const SERVER_SIDE_SDK_KEYS = serverSideEntries.map(([key]) => key) as readonly SdkKey[];
export const SERVER_SIDE_SDKS = serverSideEntries.map(([_, sdk]) => sdk) as readonly SdkInfo[];

/* Client-side SDKs */
const clientSideEntries = Object.entries(SDK_INFO).filter(([_, sdk]) => sdk.sdkType === 'Client-side');
export const CLIENT_SIDE_SDK_KEYS = clientSideEntries.map(([key]) => key) as readonly SdkKey[];
export const CLIENT_SIDE_SDKS = clientSideEntries.map(([_, sdk]) => sdk) as readonly SdkInfo[];

/* Edge SDKs */
const edgeEntries = Object.entries(SDK_INFO).filter(([_, sdk]) => sdk.sdkType === 'Edge');
export const EDGE_SDK_KEYS = edgeEntries.map(([key]) => key) as readonly SdkKey[];
export const EDGE_SDKS = edgeEntries.map(([_, sdk]) => sdk) as readonly SdkInfo[];

/* All SDKs */
export const ALL_SDK_KEYS = Object.keys(SDK_INFO) as readonly SdkKey[];
export const ALL_SDKS = Object.values(SDK_INFO) as readonly SdkInfo[];

/* Type for SDK keys (all available SDK identifiers) */
export type SdkKey = keyof typeof SDK_INFO;

/**
 * @deprecated
 *
 * Please use `SdkKey`
 *
 */
export type SdkKebabName = SdkKey;

/* Re-export types from types.ts for convenience */
export type { SdkInfo, SdkType, SdkInitKey, SnippetLang, SdkTag } from './types';

/* Type for popular SDK keys */
export type PopularSdkKey = (typeof POPULAR_SDK_KEYS)[number];

/* Type guard to check if a string is a valid SDK key */
export function isSdkKey(key: string): key is SdkKey {
  return key in SDK_INFO;
}

/* Get SDK by key  */
export function getSdkInfoByKey(key: SdkKey): SdkInfo {
  return SDK_INFO[key];
}

/* Get install snippet for a specific package manager */
export function getSdkInstallSnippet(sdkKey: SdkKey, packageManager: string): string {
  const sdkInfo = SDK_INFO[sdkKey] as SdkInfo;
  const snippet = sdkInfo.installSnippets?.[packageManager];
  return typeof snippet === 'string' ? snippet : '';
}

/* Get flag evaluation snippet for a specific SDK */
export function getSdkFlagEvalSnippet(sdkKey: SdkKey): string {
  const sdkInfo = SDK_INFO[sdkKey] as SdkInfo;
  const snippet = sdkInfo.flagEvalSnippet;
  return typeof snippet === 'string' ? snippet : '';
}

const POPULAR_SDK_KEYS_SET = new Set(POPULAR_SDK_KEYS);

/* Check if a SDK is popular */
export function isPopularSdk(key: SdkKey): key is PopularSdkKey {
  return (POPULAR_SDK_KEYS_SET as Set<SdkKey>).has(key);
}

export { default as sdkInstallPromptTemplate } from './snippets/prompt.txt?raw';
